import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IndustryDto } from '@/services/ws-crm/src/main/resources/rest-api';
import { getIndustries } from '@/services/ws-crm/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY } from 'src/constants';
import type { Option } from 'src/types';
import { formatIndustryOptionLabel } from '../helpers';

type UseIndustriesOptionsReturn = {
  options: Option<IndustryDto>[];
  pending: boolean;
};

type UseIndustriesOptions = () => UseIndustriesOptionsReturn;

export const useIndustriesOptions: UseIndustriesOptions = () => {
  const [, { language }] = useTranslation(['backend']);

  const getIndustriesRequest = useRequest({
    apiMethod: getIndustries,
    params: {},
    debounce: DEFAULT_DEBOUNCE_DELAY,
  });

  const options = useSubscribe(
    getIndustriesRequest.data$,
    (response) =>
      response?.data.map((item) => ({
        label: formatIndustryOptionLabel(item, language),
        value: item,
      })) ?? [],
    [language],
  );
  const pending = useSubscribe(getIndustriesRequest.pending$);

  return React.useMemo<UseIndustriesOptionsReturn>(
    () => ({
      options,
      pending,
    }),
    [options, pending],
  );
};
